import { SkipToken, skipToken } from "@reduxjs/toolkit/dist/query";
import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Redirect, Route, RouteComponentProps, RouteProps } from "react-router-dom";
import { ApplicationState } from ".";
import { useGetMeQuery } from "./api/rtk/MeApi";
import { CandidateViewModel } from "./model/CandidateViewModel";
import { PersonType } from "./model/PersonType";
import { SessionState } from "./store/session/SessionState";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

type RouteComponent = React.FunctionComponent<RouteComponentProps<{}>> | React.ComponentClass<any>;

type PrivateRouteProps = {
  personTypesAllowed: PersonType[];
} & SessionState
  & RouteProps;

const PrivateRoute: React.FunctionComponent<PrivateRouteProps> = ({
  component,
  isUserAuthenticated,
  isLoadingAuth,
  personTypesAllowed,
  ...rest
}) => {
  const [skipTokenState, setSkipTokenState] = useState<SkipToken | undefined>(skipToken);
  const [pathState, setPathState] = useState(rest.path);
  const { data: me  } = useGetMeQuery(skipTokenState);
  const firstRender = React.useRef(true);
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // That fixes the issue when a component dismounts and the useGetMeQuery is still pending throwing an error.
  React.useEffect(() => {
    if ((pathState !== rest.path || firstRender.current) && inProgress === InteractionStatus.None) {
      setSkipTokenState(undefined);
      setPathState(rest.path);
      firstRender.current = false;
      return;
    }
  }, [rest.path, pathState, inProgress, me]);

  const renderFn =
    (Component?: RouteComponent, me?: CandidateViewModel) => (props: RouteComponentProps) => {

      if (!Component) {
        return null;
      }

      if (inProgress !== InteractionStatus.None) {
        return <LinearProgress variant="query" />;
      }

      if (isAuthenticated && !me) {
        return null;
      }

      const isInAllowedUserTypes = _.includes(personTypesAllowed, me?.type || PersonType.Undefined);

      if (isAuthenticated && isInAllowedUserTypes) {
        return <Component {...props} />;
      }

      const routeAfterLogin = props.location ? `${props.location.pathname}${props.location.search ?? ""}` : "/";

      const redirectProps = {
        to: {
          pathname: `/login`,
          state: { from: routeAfterLogin },
        },
      };

      return <Redirect {...redirectProps} />;
    };

  return <Route {...rest} render={renderFn(component, me)} />;
};

export default connect((state: ApplicationState) => state.sessionState, null, null, { pure: false })(PrivateRoute);
